import React, { useState, useEffect } from 'react';
import { FiTruck, FiPlus, FiEdit, FiTrash2, FiSave, FiX, FiGlobe, FiUser, FiShoppingBag, FiPackage, FiEye } from 'react-icons/fi';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API_ENDPOINTS } from '../../utils/api';

const Distributors = () => {
  const [distributors, setDistributors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedDistributor, setSelectedDistributor] = useState(null);
  const [viewDistributor, setViewDistributor] = useState(null);
  const [formData, setFormData] = useState({
    companyName: '',
    website: '',
    companyDetails: '',
    products: [{ name: '', description: '' }],
    vendorDetails: [{
      vendorName: '',
      vendorCode: '',
      contactPerson: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      licenseNumber: '',
      gstNumber: '',
      panNumber: ''
    }],
    contactPerson: {
      name: '',
      designation: '',
      email: '',
      phone: '',
      alternatePhone: ''
    }
  });

  useEffect(() => {
    fetchDistributors();
  }, []);

  const fetchDistributors = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(API_ENDPOINTS.getDistributors, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDistributors(response.data);
    } catch (error) {
      console.error('Error fetching distributors:', error);
      Swal.fire('Error', 'Failed to fetch distributors', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleContactPersonChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      contactPerson: {
        ...prev.contactPerson,
        [name]: value
      }
    }));
  };

  const handleProductChange = (index, field, value) => {
    const updatedProducts = [...formData.products];
    updatedProducts[index][field] = value;
    setFormData(prev => ({
      ...prev,
      products: updatedProducts
    }));
  };

  const addProduct = () => {
    setFormData(prev => ({
      ...prev,
      products: [...prev.products, { name: '', description: '' }]
    }));
  };

  const removeProduct = (index) => {
    if (formData.products.length > 1) {
      const updatedProducts = formData.products.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        products: updatedProducts
      }));
    } else {
      Swal.fire('Warning', 'At least one product is required', 'warning');
    }
  };

  const handleVendorDetailChange = (index, field, value) => {
    const updatedVendors = [...formData.vendorDetails];
    updatedVendors[index][field] = value;
    setFormData(prev => ({
      ...prev,
      vendorDetails: updatedVendors
    }));
  };

  const addVendorDetail = () => {
    setFormData(prev => ({
      ...prev,
      vendorDetails: [...prev.vendorDetails, {
        vendorName: '',
        vendorCode: '',
        contactPerson: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        licenseNumber: '',
        gstNumber: '',
        panNumber: ''
      }]
    }));
  };

  const removeVendorDetail = (index) => {
    if (formData.vendorDetails.length > 1) {
      const updatedVendors = formData.vendorDetails.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        vendorDetails: updatedVendors
      }));
    } else {
      Swal.fire('Warning', 'At least one vendor detail is required', 'warning');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.companyName.trim()) {
      Swal.fire('Validation Error', 'Distributor company name is required', 'error');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const distributorData = {
        ...formData,
        products: formData.products.filter(p => p.name.trim() !== ''),
        vendorDetails: formData.vendorDetails.filter(v => v.vendorName.trim() !== '')
      };

      if (selectedDistributor) {
        await axios.put(API_ENDPOINTS.updateDistributor(selectedDistributor._id), distributorData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        Swal.fire('Success', 'Distributor updated successfully', 'success');
      } else {
        await axios.post(API_ENDPOINTS.createDistributor, distributorData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        Swal.fire('Success', 'Distributor created successfully', 'success');
      }

      setShowModal(false);
      resetForm();
      fetchDistributors();
    } catch (error) {
      console.error('Error saving distributor:', error);
      const errorMessage = error.response?.data?.error || 'Failed to save distributor';
      Swal.fire('Error', errorMessage, 'error');
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This action cannot be undone',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it'
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(API_ENDPOINTS.deleteDistributor(id), {
          headers: { Authorization: `Bearer ${token}` }
        });
        Swal.fire('Deleted', 'Distributor has been deleted', 'success');
        fetchDistributors();
      } catch (error) {
        console.error('Error deleting distributor:', error);
        Swal.fire('Error', 'Failed to delete distributor', 'error');
      }
    }
  };

  const handleEdit = (distributor) => {
    setSelectedDistributor(distributor);
    setFormData({
      companyName: distributor.companyName || '',
      website: distributor.website || '',
      companyDetails: distributor.companyDetails || '',
      products: distributor.products?.length > 0 
        ? distributor.products 
        : [{ name: '', description: '' }],
      vendorDetails: distributor.vendorDetails?.length > 0 
        ? distributor.vendorDetails 
        : [{
            vendorName: '',
            vendorCode: '',
            contactPerson: '',
            email: '',
            phone: '',
            address: '',
            city: '',
            state: '',
            zipCode: '',
            licenseNumber: '',
            gstNumber: '',
            panNumber: ''
          }],
      contactPerson: distributor.contactPerson || {
        name: '',
        designation: '',
        email: '',
        phone: '',
        alternatePhone: ''
      }
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setSelectedDistributor(null);
    setFormData({
      companyName: '',
      website: '',
      companyDetails: '',
      products: [{ name: '', description: '' }],
      vendorDetails: [{
        vendorName: '',
        vendorCode: '',
        contactPerson: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        licenseNumber: '',
        gstNumber: '',
        panNumber: ''
      }],
      contactPerson: {
        name: '',
        designation: '',
        email: '',
        phone: '',
        alternatePhone: ''
      }
    });
  };

  const openModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <FiTruck className="text-blue-600" />
            Distributor Companies
          </h1>
          <p className="text-gray-600 mt-1">Manage distributor company details, vendors, and contacts</p>
        </div>
        <button
          onClick={openModal}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <FiPlus className="w-5 h-5" />
          Add Distributor
        </button>
      </div>

      {/* Distributors Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : distributors.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <FiTruck className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p>No distributor companies found</p>
            <button
              onClick={openModal}
              className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
            >
              Add your first distributor
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Website</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact Person</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Products</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendors</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {distributors.map((distributor) => (
                  <tr key={distributor._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{distributor.companyName}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {distributor.website ? (
                        <a 
                          href={distributor.website.startsWith('http') ? distributor.website : `https://${distributor.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        >
                          <FiGlobe className="w-4 h-4" />
                          {distributor.website}
                        </a>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {distributor.contactPerson?.name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {distributor.contactPerson?.email || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {distributor.contactPerson?.phone || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      <div className="flex items-center gap-1">
                        <FiPackage className="w-4 h-4 text-gray-400" />
                        <span>{distributor.products?.length || 0} products</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      <div className="flex items-center gap-1">
                        <FiShoppingBag className="w-4 h-4 text-gray-400" />
                        <span>{distributor.vendorDetails?.length || 0} vendors</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => {
                          setViewDistributor(distributor);
                          setShowViewModal(true);
                        }}
                        className="text-green-600 hover:text-green-900 mr-4"
                        title="View"
                      >
                        <FiEye className="w-5 h-5 inline" />
                      </button>
                      <button
                        onClick={() => handleEdit(distributor)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                        title="Edit"
                      >
                        <FiEdit className="w-5 h-5 inline" />
                      </button>
                      <button
                        onClick={() => handleDelete(distributor._id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <FiTrash2 className="w-5 h-5 inline" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal for Add/Edit */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-800">
                {selectedDistributor ? 'Edit Distributor' : 'Add Distributor'}
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Company Information */}
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <FiTruck className="w-5 h-5 text-blue-600" />
                  Company Information
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Distributor Company Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter company name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                      <FiGlobe className="w-4 h-4" />
                      Website
                    </label>
                    <input
                      type="url"
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="https://www.example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company Details
                    </label>
                    <textarea
                      name="companyDetails"
                      value={formData.companyDetails}
                      onChange={handleInputChange}
                      rows="4"
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter company details, description, address, etc."
                    />
                  </div>
                </div>
              </div>

              {/* Products List */}
              <div className="border-b pb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <FiPackage className="w-5 h-5 text-blue-600" />
                    Products List
                  </h3>
                  <button
                    type="button"
                    onClick={addProduct}
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    <FiPlus className="w-4 h-4" />
                    Add Product
                  </button>
                </div>
                <div className="space-y-4">
                  {formData.products.map((product, index) => (
                    <div key={index} className="grid grid-cols-1 md:grid-cols-5 gap-4 p-4 bg-gray-50 rounded-lg">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
                        <input
                          type="text"
                          value={product.name}
                          onChange={(e) => handleProductChange(index, 'name', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Enter product name"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <input
                          type="text"
                          value={product.description}
                          onChange={(e) => handleProductChange(index, 'description', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Product description"
                        />
                      </div>
                      <div className="flex items-end">
                        <button
                          type="button"
                          onClick={() => removeProduct(index)}
                          className="w-full p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                          disabled={formData.products.length === 1}
                        >
                          <FiTrash2 className="w-4 h-4 mx-auto" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Vendor Details */}
              <div className="border-b pb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <FiShoppingBag className="w-5 h-5 text-blue-600" />
                    Vendor Details
                  </h3>
                  <button
                    type="button"
                    onClick={addVendorDetail}
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    <FiPlus className="w-4 h-4" />
                    Add Vendor
                  </button>
                </div>
                <div className="space-y-4">
                  {formData.vendorDetails.map((vendor, index) => (
                    <div key={index} className="p-4 bg-gray-50 rounded-lg space-y-4">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="text-sm font-semibold text-gray-700">Vendor {index + 1}</h4>
                        {formData.vendorDetails.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeVendorDetail(index)}
                            className="text-red-600 hover:text-red-700 text-sm"
                          >
                            <FiTrash2 className="w-4 h-4 inline mr-1" />
                            Remove
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Name</label>
                          <input
                            type="text"
                            value={vendor.vendorName}
                            onChange={(e) => handleVendorDetailChange(index, 'vendorName', e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Vendor name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Code</label>
                          <input
                            type="text"
                            value={vendor.vendorCode}
                            onChange={(e) => handleVendorDetailChange(index, 'vendorCode', e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Vendor code"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
                          <input
                            type="text"
                            value={vendor.contactPerson}
                            onChange={(e) => handleVendorDetailChange(index, 'contactPerson', e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Contact person name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                          <input
                            type="email"
                            value={vendor.email}
                            onChange={(e) => handleVendorDetailChange(index, 'email', e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="email@example.com"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                          <input
                            type="tel"
                            value={vendor.phone}
                            onChange={(e) => handleVendorDetailChange(index, 'phone', e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="+91 1234567890"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">License Number</label>
                          <input
                            type="text"
                            value={vendor.licenseNumber}
                            onChange={(e) => handleVendorDetailChange(index, 'licenseNumber', e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="License number"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                          <input
                            type="text"
                            value={vendor.address}
                            onChange={(e) => handleVendorDetailChange(index, 'address', e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Street address"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                          <input
                            type="text"
                            value={vendor.city}
                            onChange={(e) => handleVendorDetailChange(index, 'city', e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="City"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                          <input
                            type="text"
                            value={vendor.state}
                            onChange={(e) => handleVendorDetailChange(index, 'state', e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="State"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Zip Code</label>
                          <input
                            type="text"
                            value={vendor.zipCode}
                            onChange={(e) => handleVendorDetailChange(index, 'zipCode', e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Zip code"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">GST Number</label>
                          <input
                            type="text"
                            value={vendor.gstNumber}
                            onChange={(e) => handleVendorDetailChange(index, 'gstNumber', e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="GST number"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">PAN Number</label>
                          <input
                            type="text"
                            value={vendor.panNumber}
                            onChange={(e) => handleVendorDetailChange(index, 'panNumber', e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="PAN number"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contact Person Details */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <FiUser className="w-5 h-5 text-blue-600" />
                  Contact Person Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contact Person Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.contactPerson.name}
                      onChange={handleContactPersonChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Full name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Designation
                    </label>
                    <input
                      type="text"
                      name="designation"
                      value={formData.contactPerson.designation}
                      onChange={handleContactPersonChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., Sales Manager, Director"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.contactPerson.email}
                      onChange={handleContactPersonChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="email@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.contactPerson.phone}
                      onChange={handleContactPersonChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="+91 1234567890"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Alternate Phone
                    </label>
                    <input
                      type="tel"
                      name="alternatePhone"
                      value={formData.contactPerson.alternatePhone}
                      onChange={handleContactPersonChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="+91 1234567890"
                    />
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end gap-4 pt-4 border-t">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <FiSave className="w-5 h-5" />
                  {selectedDistributor ? 'Update Distributor' : 'Save Distributor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && viewDistributor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-white">
                Distributor Details
              </h2>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setViewDistributor(null);
                }}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-1 transition-colors"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Company Information */}
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <FiTruck className="w-5 h-5 text-blue-600" />
                  Company Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Company Name</label>
                    <p className="text-base font-semibold text-gray-900">{viewDistributor.companyName || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1 flex items-center gap-2">
                      <FiGlobe className="w-4 h-4" />
                      Website
                    </label>
                    <p className="text-base text-gray-900">
                      {viewDistributor.website ? (
                        <a 
                          href={viewDistributor.website.startsWith('http') ? viewDistributor.website : `https://${viewDistributor.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800"
                        >
                          {viewDistributor.website}
                        </a>
                      ) : '-'}
                    </p>
                  </div>
                  {viewDistributor.companyDetails && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-500 mb-1">Company Details</label>
                      <p className="text-base text-gray-900 whitespace-pre-wrap">{viewDistributor.companyDetails}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Products List */}
              {viewDistributor.products && viewDistributor.products.length > 0 && (
                <div className="border-b pb-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <FiPackage className="w-5 h-5 text-blue-600" />
                    Products List ({viewDistributor.products.length})
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 border">Product Name</th>
                          <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 border">Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewDistributor.products.map((product, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-2 text-sm text-gray-900 border">{product.name}</td>
                            <td className="px-4 py-2 text-sm text-gray-700 border">{product.description || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Vendor Details */}
              {viewDistributor.vendorDetails && viewDistributor.vendorDetails.length > 0 && (
                <div className="border-b pb-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <FiShoppingBag className="w-5 h-5 text-blue-600" />
                    Vendor Details ({viewDistributor.vendorDetails.length})
                  </h3>
                  <div className="space-y-4">
                    {viewDistributor.vendorDetails.map((vendor, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                        <h4 className="text-sm font-semibold text-gray-800 mb-3">Vendor {index + 1}</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          {vendor.vendorName && (
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-1">Vendor Name</label>
                              <p className="text-sm font-semibold text-gray-900">{vendor.vendorName}</p>
                            </div>
                          )}
                          {vendor.vendorCode && (
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-1">Vendor Code</label>
                              <p className="text-sm text-gray-900 font-mono">{vendor.vendorCode}</p>
                            </div>
                          )}
                          {vendor.contactPerson && (
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-1">Contact Person</label>
                              <p className="text-sm text-gray-900">{vendor.contactPerson}</p>
                            </div>
                          )}
                          {vendor.email && (
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
                              <p className="text-sm text-gray-900">{vendor.email}</p>
                            </div>
                          )}
                          {vendor.phone && (
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-1">Phone</label>
                              <p className="text-sm text-gray-900">{vendor.phone}</p>
                            </div>
                          )}
                          {vendor.licenseNumber && (
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-1">License Number</label>
                              <p className="text-sm text-gray-900">{vendor.licenseNumber}</p>
                            </div>
                          )}
                          {vendor.address && (
                            <div className="md:col-span-2">
                              <label className="block text-xs font-medium text-gray-500 mb-1">Address</label>
                              <p className="text-sm text-gray-900">{vendor.address}</p>
                            </div>
                          )}
                          {(vendor.city || vendor.state || vendor.zipCode) && (
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-1">City, State, Zip</label>
                              <p className="text-sm text-gray-900">
                                {[vendor.city, vendor.state, vendor.zipCode].filter(Boolean).join(', ') || '-'}
                              </p>
                            </div>
                          )}
                          {vendor.gstNumber && (
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-1">GST Number</label>
                              <p className="text-sm text-gray-900 font-mono">{vendor.gstNumber}</p>
                            </div>
                          )}
                          {vendor.panNumber && (
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-1">PAN Number</label>
                              <p className="text-sm text-gray-900 font-mono">{vendor.panNumber}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Contact Person Details */}
              {viewDistributor.contactPerson && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <FiUser className="w-5 h-5 text-blue-600" />
                    Contact Person Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {viewDistributor.contactPerson.name && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Contact Person Name</label>
                        <p className="text-base font-semibold text-gray-900">{viewDistributor.contactPerson.name}</p>
                      </div>
                    )}
                    {viewDistributor.contactPerson.designation && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Designation</label>
                        <p className="text-base text-gray-900">{viewDistributor.contactPerson.designation}</p>
                      </div>
                    )}
                    {viewDistributor.contactPerson.email && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Email</label>
                        <p className="text-base text-gray-900">{viewDistributor.contactPerson.email}</p>
                      </div>
                    )}
                    {viewDistributor.contactPerson.phone && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Phone</label>
                        <p className="text-base text-gray-900">{viewDistributor.contactPerson.phone}</p>
                      </div>
                    )}
                    {viewDistributor.contactPerson.alternatePhone && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Alternate Phone</label>
                        <p className="text-base text-gray-900">{viewDistributor.contactPerson.alternatePhone}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Distributors;
